import type { TrackingAdapterSummary, TrackingIntegration } from '@chukua/common';
import { Table } from 'antd';
import { Plug, SearchX, ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { useInstalledIntegrations } from '../hooks/use-installed-integrations';
import { integrationPath } from '../integrations.paths';
import { buildIntegrationColumns } from './build-integration-columns';

/** Installed integrations with status, searched, filtered, sorted and paged on the server. A row opens its edit sheet. */
export function InstalledIntegrationsTable({ adapters }: { adapters: TrackingAdapterSummary[] }) {
  const installed = useInstalledIntegrations();
  const navigate = useNavigate();
  const { list, state } = installed;

  if (state.status === 'loading') return <TableSkeleton label="Loading installed integrations" withSearch />;
  if (state.status === 'error' || state.status === 'forbidden') {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="Couldn't load integrations"
        description="Something went wrong loading the installed integrations. Try reloading the page."
      />
    );
  }
  if (state.page.total === 0 && !list.isFiltered) {
    return <EmptyState icon={Plug} title="Nothing installed yet" description="Install a GPS adapter from the catalogue with your company's credentials." />;
  }

  const columns = buildIntegrationColumns({
    list,
    adapterOptions: adapters.map((adapter) => ({ text: adapter.displayName, value: adapter.key })),
    onEnable: installed.enable,
    onDisable: installed.disable,
    onUninstall: installed.uninstall,
  });

  return (
    <div className="flex flex-col gap-4">
      <ListSearch value={list.query.q} placeholder="Search name or adapter" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
      <Table<TrackingIntegration>
        rowKey="id"
        dataSource={state.page.items}
        columns={columns}
        loading={installed.isRefreshing}
        scroll={{ x: 'max-content' }}
        onRow={(integration) => ({
          onClick: () => integration.status !== 'uninstalled' && navigate(integrationPath(integration.id)),
          className: integration.status === 'uninstalled' ? undefined : 'cursor-pointer',
        })}
        locale={
          list.isFiltered
            ? { emptyText: <EmptyState icon={SearchX} title="No matching integrations" description="No integrations match this search or these filters." /> }
            : undefined
        }
        {...list.tableProps<TrackingIntegration>(state.page.total)}
      />
    </div>
  );
}
